/**
 * PharmaLabReport.jsx — Pharma / Healthcare Research Report
 * 
 * Specialized reporting layer for pharma, healthcare, nutraceuticals.
 * Consumes the same simulation data, interpreted through a clinical lens.
 *
 * Sections:
 * 1. Medical Context / Executive Snapshot
 * 2. Treatment Perception
 * 3. Adoption Pathway
 * 4. Adherence Risk
 * 5. Safety Concerns
 * 6. Patient Segments
 * 7. Clinical Market Potential Score
 * 8. Strategic Implications + Decision Dashboard
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Separator } from './ui/separator';
import {
  Activity, Shield, Heart, AlertTriangle, TrendingUp, TrendingDown,
  Users, CheckCircle2, XCircle, Minus, Loader2, Target, ArrowDown
} from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../lib/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart as RechartsPie, Pie, Legend
} from 'recharts';

const RISK_COLORS = { low: '#10B981', moderate: '#F59E0B', high: '#EF4444', unknown: '#94A3B8' };
const DECISION_COLORS = { proceed: '#10B981', refine: '#F59E0B', reconsider: '#EF4444' };
const PERCEPTION_COLORS = { favorable: '#10B981', mixed: '#F59E0B', unfavorable: '#EF4444', unknown: '#94A3B8' };
const PIE_COLORS = ['#10B981', '#F59E0B', '#EF4444', '#6366F1', '#0EA5E9', '#F43F5E', '#8B5CF6'];

function ScoreGauge({ score, label, size = 'md' }) {
  const radius = size === 'lg' ? 54 : 40;
  const stroke = size === 'lg' ? 8 : 6;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = score >= 65 ? '#10B981' : score >= 40 ? '#F59E0B' : '#EF4444';

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={radius * 2 + stroke * 2} height={radius * 2 + stroke * 2} className="transform -rotate-90">
        <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none"
          stroke="currentColor" className="text-muted/20" strokeWidth={stroke} />
        <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none"
          stroke={color} strokeWidth={stroke} strokeDasharray={circumference}
          strokeDashoffset={circumference - progress} strokeLinecap="round"
          className="transition-all duration-1000" />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: radius * 2, height: radius * 2 }}>
        <span className={cn("font-bold", size === 'lg' ? 'text-2xl' : 'text-lg')} style={{ color }}>{Math.round(score)}</span>
      </div>
      {label && <span className="text-xs text-muted-foreground mt-1">{label}</span>}
    </div>
  );
}

function RiskBadge({ level }) {
  return (
    <Badge data-testid={`risk-badge-${level}`} className={cn(
      "capitalize",
      level === 'low' && "bg-positive-soft  text-positive-soft-foreground ",
      level === 'moderate' && "bg-warning-soft  text-warning-soft-foreground ",
      level === 'high' && "bg-negative-soft  text-negative-soft-foreground ",
      level === 'unknown' && "bg-slate-100 dark:bg-slate-500/20 text-slate-700 dark:text-slate-300",
    )}>
      {level === 'low' && <CheckCircle2 className="w-3 h-3 mr-1" />}
      {level === 'moderate' && <AlertTriangle className="w-3 h-3 mr-1" />}
      {level === 'high' && <XCircle className="w-3 h-3 mr-1" />}
      {level} risk
    </Badge>
  );
}

export default function PharmaLabReport({ simulation }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!simulation?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.get(`/pharmalab/simulations/${simulation.id}/report`, { _silent: true })
      .then(res => { if (!cancelled) setReport(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.detail || 'Failed to load PharmaLab report'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [simulation?.id]);

  if (!simulation) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="pharmalab-loading">
        <Loader2 className="w-6 h-6 animate-spin text-positive mr-2" />
        <span className="text-muted-foreground">Generating PharmaLab report...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-negative-border" data-testid="pharmalab-error">
        <CardContent className="py-8 text-center">
          <AlertTriangle className="w-8 h-8 text-negative mx-auto mb-2" />
          <p className="text-negative-soft-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!report) return null;

  const { medical_context, treatment_perception, adoption_pathway, adherence_risk,
    safety_concerns, patient_segments, clinical_potential, strategic_implications,
    decision, kpi_score, response_scale } = report;

  const distData = treatment_perception?.distribution
    ? Object.entries(treatment_perception.distribution).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div className="space-y-6" data-testid="pharmalab-report">
      {/* Section 1: Executive Snapshot */}
      <Card className="border-positive-border bg-gradient-to-br from-emerald-50/50 to-white dark:from-emerald-950/20 dark:to-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-positive-soft-foreground" />
            <CardTitle className="text-lg text-foreground">Medical Context</CardTitle>
            <Badge variant="outline" className="ml-auto text-xs">PharmaLab</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 rounded-lg bg-background dark:bg-muted/30 border border-border">
              <p className="text-2xl font-bold text-positive-soft-foreground">{medical_context?.total_responses || 0}</p>
              <p className="text-xs text-muted-foreground">Sample Size</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-background dark:bg-muted/30 border border-border">
              <p className="text-2xl font-bold" style={{ color: (medical_context?.headline_score || 0) >= 60 ? '#10B981' : (medical_context?.headline_score || 0) >= 40 ? '#F59E0B' : '#EF4444' }}>
                {medical_context?.headline_score || 0}
              </p>
              <p className="text-xs text-muted-foreground">Headline Score</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-background dark:bg-muted/30 border border-border">
              <p className="text-2xl font-bold text-positive-soft-foreground">{medical_context?.positive_pct || 0}%</p>
              <p className="text-xs text-muted-foreground">Favorable</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-background dark:bg-muted/30 border border-border">
              <p className="text-2xl font-bold text-foreground">{Math.round((medical_context?.avg_confidence || 0) * 100)}%</p>
              <p className="text-xs text-muted-foreground">Confidence</p>
            </div>
          </div>
          {medical_context?.kpi_mapping?.kpi_tag && (
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Target className="w-3.5 h-3.5" />
              <span>KPI: <strong className="text-foreground capitalize">{medical_context.kpi_mapping.kpi_tag.replace('_', ' ')}</strong></span>
              <Badge variant="outline" className="text-[10px]">{medical_context.kpi_mapping.mapping_status}</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Treatment Perception */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-negative" />
            <CardTitle className="text-base text-foreground">Treatment Perception</CardTitle>
            <Badge className={cn("ml-auto text-xs capitalize", PERCEPTION_COLORS[treatment_perception?.perception] ? '' : '')}
              style={{ backgroundColor: `${PERCEPTION_COLORS[treatment_perception?.perception || 'unknown']}20`, color: PERCEPTION_COLORS[treatment_perception?.perception || 'unknown'] }}>
              {treatment_perception?.perception || 'N/A'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Favorability Index</span>
                    <span className="font-medium text-foreground">{treatment_perception?.favorability || 0}</span>
                  </div>
                  <Progress value={Math.max(0, Math.min(100, (treatment_perception?.favorability || 0) + 50))} className="h-2" />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="p-2.5 rounded-lg bg-positive-soft border border-positive-border">
                    <p className="text-lg font-bold text-positive-soft-foreground">{treatment_perception?.positive_pct || 0}%</p>
                    <p className="text-xs text-positive-soft-foreground">Favorable</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-negative-soft border border-negative-border">
                    <p className="text-lg font-bold text-negative-soft-foreground">{treatment_perception?.negative_pct || 0}%</p>
                    <p className="text-xs text-negative-soft-foreground">Unfavorable</p>
                  </div>
                </div>
              </div>
            </div>
            {distData.length > 0 && (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie data={distData} cx="50%" cy="50%" outerRadius="75%" innerRadius="40%"
                      dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                      {distData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Adoption Pathway */}
      {adoption_pathway?.length > 0 && (
        <Card className="border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-500" />
              <CardTitle className="text-base text-foreground">Adoption Pathway</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 justify-center">
              {adoption_pathway.map((stage, i) => (
                <div key={stage.stage} className="flex flex-col items-center gap-1 flex-1">
                  <span className="text-sm font-semibold text-foreground">{stage.percentage}%</span>
                  <div className="w-full rounded-t-md transition-all" style={{
                    height: `${Math.max(20, stage.percentage * 1.5)}px`,
                    backgroundColor: ['#6366F1', '#10B981', '#0EA5E9', '#F59E0B'][i] || '#94A3B8',
                    opacity: 0.85,
                  }} />
                  <span className="text-[10px] text-muted-foreground capitalize text-center">{stage.stage}</span>
                  {i < adoption_pathway.length - 1 && (
                    <ArrowDown className="w-3 h-3 text-muted-foreground/50 absolute" style={{ display: 'none' }} />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Section 4: Adherence Risk */}
        <Card className="border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-warning" />
                <CardTitle className="text-base text-foreground">Adherence Risk</CardTitle>
              </div>
              <RiskBadge level={adherence_risk?.risk_level || 'unknown'} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Dropout Risk</span>
                  <span className="font-medium text-foreground">{adherence_risk?.dropout_risk_pct || 0}%</span>
                </div>
                <Progress value={adherence_risk?.dropout_risk_pct || 0} className="h-2" />
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 rounded bg-muted/30">
                  <p className="text-muted-foreground">Negative</p>
                  <p className="font-medium text-foreground">{adherence_risk?.negative_pct || 0}%</p>
                </div>
                <div className="p-2 rounded bg-muted/30">
                  <p className="text-muted-foreground">Neutral</p>
                  <p className="font-medium text-foreground">{adherence_risk?.neutral_pct || 0}%</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 5: Safety Concerns */}
        <Card className="border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                <CardTitle className="text-base text-foreground">Safety Concerns</CardTitle>
              </div>
              <RiskBadge level={safety_concerns?.risk_level || 'unknown'} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Concern Rate</span>
                  <span className="font-medium text-foreground">{safety_concerns?.concern_pct || 0}%</span>
                </div>
                <Progress value={safety_concerns?.concern_pct || 0} className="h-2" />
              </div>
              <div className="text-sm text-muted-foreground">
                {safety_concerns?.concerned_count || 0} of {safety_concerns?.total || 0} respondents expressed concerns
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section 6: Patient Segments */}
      {patient_segments && Object.keys(patient_segments).length > 0 && (
        <Card className="border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              <CardTitle className="text-base text-foreground">Patient Segments</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(patient_segments).map(([dimension, segments]) => (
                <div key={dimension}>
                  <p className="text-sm font-medium text-muted-foreground capitalize mb-2">{dimension}</p>
                  <div className="space-y-2">
                    {segments.map((seg) => (
                      <div key={seg.segment} className="flex items-center gap-3 text-sm">
                        <span className="w-24 truncate text-foreground font-medium">{seg.segment}</span>
                        <div className="flex-1 flex gap-0.5 h-5 rounded-full overflow-hidden">
                          <div className="bg-positive transition-all" style={{ width: `${seg.positive_pct}%` }} title={`Favorable: ${seg.positive_pct}%`} />
                          <div className="bg-slate-400 transition-all" style={{ width: `${seg.neutral_pct}%` }} title={`Neutral: ${seg.neutral_pct}%`} />
                          <div className="bg-negative transition-all" style={{ width: `${seg.negative_pct}%` }} title={`Unfavorable: ${seg.negative_pct}%`} />
                        </div>
                        <span className="w-16 text-right text-positive-soft-foreground font-medium">{seg.positive_pct}%</span>
                        <span className="w-10 text-right text-muted-foreground text-xs">n={seg.count}</span>
                      </div>
                    ))}
                  </div>
                  <Separator className="mt-3" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 7: Clinical Market Potential Score */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-positive" />
            <CardTitle className="text-base text-foreground">Clinical Market Potential Score</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-8">
            <div className="relative flex items-center justify-center">
              <ScoreGauge score={clinical_potential?.score || 0} size="lg" />
            </div>
            <div className="flex-1 space-y-2">
              {clinical_potential?.components && Object.entries(clinical_potential.components).map(([kpi, data]) => (
                <div key={kpi} className="flex items-center gap-2">
                  <span className="w-32 text-sm text-muted-foreground capitalize">{kpi.replace('_', ' ')}</span>
                  <div className="flex-1">
                    <Progress value={data.score || 0} className="h-1.5" />
                  </div>
                  <span className="w-12 text-right text-sm font-medium text-foreground">{data.score || 0}</span>
                  <span className="w-10 text-right text-xs text-muted-foreground">w:{data.weight}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 8: Strategic Implications + Decision Dashboard */}
      {strategic_implications?.length > 0 && (
        <Card className="border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-foreground">Strategic Implications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {strategic_implications.map((imp, i) => (
                <div key={i} className={cn("p-3 rounded-lg border", 
                  imp.type === 'positive' && "bg-positive-soft  border-positive-border ",
                  imp.type === 'moderate' && "bg-warning-soft  border-warning-border ",
                  imp.type === 'negative' && "bg-negative-soft  border-negative-border ",
                  imp.type === 'warning' && "bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-800",
                )}>
                  <p className="font-medium text-sm text-foreground">{imp.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{imp.detail}</p>
                  <p className="text-xs font-medium mt-1.5" style={{ color: imp.type === 'positive' ? '#10B981' : imp.type === 'warning' ? '#F59E0B' : '#64748B' }}>
                    Action: {imp.action}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Decision Dashboard */}
      {decision && (
        <Card className={cn("border-2", 
          decision.recommendation === 'proceed' && "border-positive-border ",
          decision.recommendation === 'refine' && "border-warning-border ",
          decision.recommendation === 'reconsider' && "border-negative-border ",
        )}>
          <CardContent className="py-6">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white font-semibold"
                style={{ backgroundColor: DECISION_COLORS[decision.recommendation] || '#94A3B8' }}>
                {decision.recommendation === 'proceed' && <CheckCircle2 className="w-5 h-5" />}
                {decision.recommendation === 'refine' && <AlertTriangle className="w-5 h-5" />}
                {decision.recommendation === 'reconsider' && <XCircle className="w-5 h-5" />}
                {decision.label}
              </div>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">{decision.rationale}</p>
              <Badge variant="outline" className="text-[10px]">Confidence: {decision.confidence}</Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
